// External libraries
import { useRecoilState } from 'recoil';
import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { useNavigate } from 'react-router-dom';

// Components
import { authState } from '../states';

// Utilities
import { ADJACENCY_RELATED_APIS, APIS, RouteConstants } from '../constants';
import { useDb } from '../indexDB/db';
import { useIDBService } from '../services';
import { applyAdjacencyMiddleware } from './utils';

// Variables to manage token refresh state and subscribers
let isRefreshing = false;
let refreshSubscribers: any[] = [];

// Function to add a callback to the subscribers array
const subscribeTokenRefresh = (cb: any) => {
    refreshSubscribers.push(cb);
}

// Function to notify all subscribers with the new token
const onRrefreshed = (token: string) => {
    refreshSubscribers?.map(cb => cb(token));
}

function useFetchWrapper() {
    const [auth, setAuth] = useRecoilState(authState);
    const navigate = useNavigate();
    const db = useDb();
    const IDBService = useIDBService();

    // Create an Axios instance with common headers
    const axiosInstance: AxiosInstance = axios.create({
        baseURL: '',
        headers: {
            'Content-Type': 'application/json',
        },
    });

    // Interceptor for adding authorization headers
    axiosInstance.interceptors.request.use(
        async (config: any) => {
            // Skip adding Authorization header for specific endpoints
            if ([APIS.USERS.LOGIN, APIS.USERS.FORGOT_PASSWORD, APIS.USERS.SET_NEW_PASSWORD].includes(config.url)) return config;

             if (ADJACENCY_RELATED_APIS.some(api => config.url.includes(api))) {
                config = applyAdjacencyMiddleware(config); // Apply adjacency middleware for specific API endpoints
            }

            const user = localStorage.getItem('user') || null;
            if (user != null) {
                let userData;
                try {
                    userData = JSON.parse(user);
                } catch (error) {
                    console.error('Error parsing user data in interceptor:', error);
                    return config;
                }

                const token = userData?.tokens?.access;
                if (!token) {
                    console.warn('No access token found in user data');
                    return config;
                }

                // Check if token is expired or expiring soon (30 second buffer)
                var isTokenExpired = checkTokenExpiry(token, 30);
                if (!isTokenExpired) {
                    // Add Authorization header if token is not expired
                    config.headers['Authorization'] = `Bearer ${token}`;
                } else {
                    // Handle token refresh
                    if (!isRefreshing) {
                        isRefreshing = true;
                        try {
                            const data = await getRefreshToken();
                            const newAccessToken = data.access;
                            const updatedAuth = {
                                ...auth,
                                tokens: {
                                    ...auth.tokens,
                                    access: newAccessToken
                                }
                            };
                            setAuth(updatedAuth);
                            localStorage.setItem('user', JSON.stringify(updatedAuth));
                            config.headers['Authorization'] = `Bearer ${newAccessToken}`;
                            onRrefreshed(newAccessToken);
                        } catch (error: any) {
                            refreshSubscribers = [];
                            ['user', 'email_id', 'user_id', 'selCategoryList'].forEach((item: string) => localStorage.removeItem(item));
                            setAuth({});
                            navigate(RouteConstants.login);
                            throw error;

                        } finally {
                            isRefreshing = false;
                            refreshSubscribers = [];
                        }
                    } else {
                        // Wait for the token to be refreshed
                        return new Promise((resolve) => {
                            subscribeTokenRefresh((newAccessToken: string) => {
                                config.headers['Authorization'] = `Bearer ${newAccessToken}`;
                                resolve(config);
                            });
                        });
                    }
                }
                return config;
            }
            return config;
        },
        (error: any) => {
            return Promise.reject(error);
        }
    );

    // Response interceptor to handle 401 errors
    axiosInstance.interceptors.response.use(
        (response) => response, // Pass through successful responses
        async (error) => {
            const originalRequest = error.config;

            // Check if error is 401 and we haven't already retried this request
            if (error.response?.status === 401 && error?.response?.data?.code=="INVALID_TOKEN" && !originalRequest._retry) {
                // Skip token refresh for login, forgot password, and set new password endpoints
                if ([APIS.USERS.LOGIN, APIS.USERS.FORGOT_PASSWORD, APIS.USERS.SET_NEW_PASSWORD].includes(originalRequest.url)) {
                    return Promise.reject(error);
                }

                originalRequest._retry = true;

                if (!isRefreshing) {
                    isRefreshing = true;
                    try {
                        const data = await getRefreshToken();
                        const newAccessToken = data.access;
                        const updatedAuth = {
                            ...auth,
                            tokens: {
                                ...auth.tokens,
                                access: newAccessToken
                            }
                        };
                        setAuth(updatedAuth);
                        localStorage.setItem('user', JSON.stringify(updatedAuth));

                        // Notify all waiting requests
                        onRrefreshed(newAccessToken);

                        // Retry the original request with new token
                        originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;
                        return axiosInstance(originalRequest);
                    } catch (refreshError) {
                        refreshSubscribers = [];
                        ['user', 'email_id', 'user_id', 'selCategoryList'].forEach((item: string) => localStorage.removeItem(item));
                        setAuth({});
                        navigate(RouteConstants.login);
                        return Promise.reject(refreshError);
                    } finally {
                        isRefreshing = false;
                        refreshSubscribers = [];
                    }
                } else {
                    // Wait for the ongoing refresh to complete
                    return new Promise((resolve) => {
                        subscribeTokenRefresh((newAccessToken: string) => {
                            originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;
                            resolve(axiosInstance(originalRequest));
                        });
                    });
                }
            }

            return Promise.reject(error);
        }
    );

    return {
        get: (url: string, params?: any, signal?: AbortSignal) => axiosInstance.get(getURL(url), { params, signal }).then(handleResponse).catch(handleError),
        post: (url: string, data?: any, signal?: AbortSignal) => axiosInstance.post(getURL(url), data, { signal }).then(handleResponse).catch(handleError),
        put: (url: string, data?: any, signal?: AbortSignal) => axiosInstance.put(getURL(url), data, { signal }).then(handleResponse).catch(handleError),
        patch: (url: string, data?: any, signal?: AbortSignal) => axiosInstance.patch(getURL(url), data, { signal }).then(handleResponse).catch(handleError),
        delete: (url: string, data?: any, signal?: AbortSignal) => axiosInstance.delete(getURL(url), { data, signal }).then(handleResponse).catch(handleError),
    };

    function getURL(url: string) {
        let host = process.env.REACT_APP_API_HOST_DEV;
        let baseURL = process.env.REACT_APP_API_BASE_URL;
        if (window.location.host === host) {
            url = baseURL + url;
        }
        return url;
    }

    async function handleResponse(response: AxiosResponse) {
        if (![APIS.USERS.LOGIN, APIS.USERS.GET_LOGGED_USER, APIS.USERS.GET_LOGGED_USER_V3, APIS.USERS.GET_NEW_ACCESS_TOKEN].some(path => response.request.responseURL.includes(path))) {
            await db.storeResponseInDB(response); // Store error responses in IndexedDB
        }

        if (![200, 201, 204, 409].includes(response.status)) {
            if ([401, 403, 500].includes(response.status) && auth?.token) {
                // auto logout if 401 Unauthorized or 403 Forbidden response returned from api
                // ['user', 'email_id', 'user_id', 'selCategoryList'].forEach((item: string) => localStorage.removeItem(item));
                localStorage.clear();
                setAuth(null);
            }
            const error = (response?.data?.detail) || response?.statusText;
            return Promise.reject(error);
        }
        return response.data;
    }

    function handleError(error: any) {
        if (error.response) {
            if (![APIS.USERS.LOGIN, APIS.USERS.GET_LOGGED_USER, APIS.USERS.GET_LOGGED_USER_V3, APIS.USERS.GET_NEW_ACCESS_TOKEN].some(path => error.response.request.responseURL.includes(path))) {
                db.storeResponseInDB(error.response); // Store error responses in IndexedDB
            }
        }
        return Promise.reject(error);
    }

    // async function getRefreshToken() {
    //     const refresh = auth?.tokens?.refresh;
    //     const access = auth?.tokens?.access;
    //     const isExpired = checkTokenExpiry(refresh);
    //     if (isExpired) {
    //         localStorage.removeItem('user');
    //         localStorage.removeItem('email_id');
    //         setAuth(null);
    //         const error = { response: { data: { detail: 'Invalid refresh token' } } }
    //         throw error;
    //     } else {
    //         const headers = {
    //             'Authorization': `Bearer ${access}`,
    //             'Content-Type': 'application/json'
    //         };
    //         const response = await axios.post(getURL(APIS.USERS.GET_NEW_ACCESS_TOKEN), { refresh }, { headers });
    //         return handleResponse(response);
    //     }
    // }

    async function getRefreshToken() {
        // Read from localStorage to ensure we have the latest tokens
        const user = localStorage.getItem('user');
        if (!user) {
            const error = { response: { data: { detail: 'No user data found' } } };
            throw error;
        }

        let userData;
        try {
            userData = JSON.parse(user);
        } catch (error) {
            console.error('Error parsing user data:', error);
            ['user', 'email_id', 'user_id', 'selCategoryList'].forEach((item: string) => localStorage.removeItem(item));
            setAuth(null);
            navigate(RouteConstants.login);
            throw error;
        }

        const refresh = userData?.tokens?.refresh;
        const access = userData?.tokens?.access;

        if (!refresh) {
            console.error('No refresh token available');
            ['user', 'email_id', 'user_id', 'selCategoryList'].forEach((item: string) => localStorage.removeItem(item));
            setAuth(null);
            navigate(RouteConstants.login);
            const error = { response: { data: { detail: 'No refresh token available' } } };
            throw error;
        }

        const isExpired = checkTokenExpiry(refresh);
        const completeUrl = APIS.USERS.GET_NEW_ACCESS_TOKEN;

        if (isExpired) {
            console.error('Refresh token expired');
            ['user', 'email_id', 'user_id', 'selCategoryList'].forEach((item: string) => localStorage.removeItem(item));
            setAuth(null);
            navigate(RouteConstants.login);
            const error = { response: { data: { detail: 'Refresh token expired' } } };
            throw error;
        }

        const headers = {
            'Authorization': `Bearer ${access}`,
            'Content-Type': 'application/json'
        };

        try {
            const response = await axios.post(getURL(completeUrl), { refresh }, { headers });

            // After successful token refresh, retrieve, post the data and clear IndexedDB
            IDBService.postUserMetrics();
            return handleResponse(response);
        } catch (error: any) {
            console.error('Error refreshing token:', error?.response?.data || error?.message || error);
            // Only logout on specific error codes, not all errors
            if (error?.response?.status === 401 || error?.response?.status === 403) {
                ['user', 'email_id', 'user_id', 'selCategoryList'].forEach((item: string) => localStorage.removeItem(item));
                setAuth(null);
                navigate(RouteConstants.login);
            }
            throw error;
        }
    }

    function checkTokenExpiry(token: any, bufferSeconds: number = 0) {
        try {
            if (!token || typeof token !== 'string') {
                return true; // Consider invalid tokens as expired
            }
            const parts = token.split('.');
            if (parts.length !== 3) {
                return true; // Invalid JWT format
            }
            const tokenData = JSON.parse(atob(parts[1]));
            const expiryTime = tokenData.exp * 1000;
            const currentTime = Date.now();
            // Add buffer time (in milliseconds) to refresh token before it actually expires
            return currentTime >= (expiryTime - bufferSeconds * 1000);
        } catch (error) {
            console.error('Error checking token expiry:', error);
            return true; // Consider tokens that can't be parsed as expired
        }
    }
}

export { useFetchWrapper };

